/**
 * The overlay renderer as a hook: the stitcher's own `template.html` in an
 * iframe, driven over postMessage (the bridge at the tail of
 * `sdk/truereel_overlay/template.html`). Every request carries an id the
 * template echoes, so replies resolve promises; `ready` flips on the
 * iframe's `load` event.
 *
 * jsdom never loads the template, so a page must work with `ready = false`
 * (requests reject at once, nothing hangs); tests that need bounds supply a
 * fake through `OverlayFrameContext`.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react";
import type { OverlayDoc } from "./overlayDoc";
import type { Aspect } from "../api/types";

/** A box in template pixels. */
export interface Box2D {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** One layer's rendered box in template pixels; for a text layer, `text` is
 *  the box of its typeset text (what the legibility check measures). */
export interface LayerBounds extends Box2D {
  id: string;
  text?: Box2D;
}

export interface LoadArgs {
  doc: OverlayDoc;
  aspect: Aspect;
  width: number;
  height: number;
  /** `{ [assetId]: url }` for image layers. */
  assets: Record<string, string>;
}

export interface OverlayFrame {
  ready: boolean;
  /** The last error the template reported, as text. */
  error: string | null;
  load(args: LoadArgs): Promise<LayerBounds[]>;
  renderAt(t: number): Promise<LayerBounds[]>;
  bounds(): Promise<LayerBounds[]>;
  /** Gives text layers their outline (`legibility.ts`): `{id: colour}`, a
   *  null colour taking it away. */
  backings?(map: Record<string, string | null>): Promise<LayerBounds[]>;
  /** Draw time `t` and expect no reply: the player calls it on every frame
   *  it draws. Does nothing before the frame is ready; no id, no promise, no
   *  timer. */
  seek(t: number): void;
  /** The captions as they are laid out now, as a self-contained SVG (the
   *  faces and pictures they use embedded): what a frame of the scene draws
   *  over the canvas, so that it carries its captions (`frames.ts`). */
  picture?(): Promise<string>;
}

/** A test seam: when set, the hook returns this instead of a real bridge. */
export const OverlayFrameContext = createContext<OverlayFrame | null>(null);

export const REQUEST_TIMEOUT_MS = 5_000;
/** A picture embeds the faces it uses: the first one fetches them. */
export const PICTURE_TIMEOUT_MS = 20_000;

interface Pending {
  resolve: (reply: Reply) => void;
  reject: (err: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

interface Reply {
  type?: unknown;
  id?: unknown;
  bounds?: LayerBounds[];
  svg?: unknown;
  error?: unknown;
}

export function useOverlayFrame(
  iframeRef: RefObject<HTMLIFrameElement>,
  targetOrigin: string,
): OverlayFrame {
  const fake = useContext(OverlayFrameContext);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pending = useRef(new Map<number, Pending>());
  const nextId = useRef(1);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const onLoad = () => setReady(true);
    iframe.addEventListener("load", onLoad);
    return () => {
      iframe.removeEventListener("load", onLoad);
      setReady(false);
    };
  }, [iframeRef]);

  useEffect(() => {
    const inflight = pending.current;
    const onMessage = (ev: MessageEvent) => {
      const win = iframeRef.current?.contentWindow;
      if (!win || ev.source !== win) return;
      const data = ev.data as Reply | null;
      if (!data || data.type !== "tr-overlay" || typeof data.id !== "number")
        return;
      const entry = inflight.get(data.id);
      if (!entry) return;
      inflight.delete(data.id);
      clearTimeout(entry.timer);
      if (data.error !== undefined) {
        const message = String(data.error);
        setError(message);
        entry.reject(new Error(message));
      } else {
        setError(null);
        entry.resolve(data);
      }
    };
    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
      for (const entry of inflight.values()) {
        clearTimeout(entry.timer);
        entry.reject(new Error("overlay renderer unmounted"));
      }
      inflight.clear();
    };
  }, [iframeRef]);

  const ask = useCallback(
    (body: Record<string, unknown>, timeoutMs = REQUEST_TIMEOUT_MS) =>
      new Promise<Reply>((resolve, reject) => {
        const win = iframeRef.current?.contentWindow;
        if (!ready || !win) {
          reject(new Error("overlay renderer not ready"));
          return;
        }
        const id = nextId.current++;
        const timer = setTimeout(() => {
          pending.current.delete(id);
          reject(new Error("overlay renderer did not answer"));
        }, timeoutMs);
        pending.current.set(id, { resolve, reject, timer });
        win.postMessage({ id, ...body }, targetOrigin);
      }),
    [iframeRef, ready, targetOrigin],
  );
  const post = useCallback(
    (body: Record<string, unknown>) =>
      ask(body).then((r) => (Array.isArray(r.bounds) ? r.bounds : [])),
    [ask],
  );

  // Stable per `post`, so effects can depend on the functions without
  // re-running whenever `error` or `ready` flips.
  const load = useCallback(
    (args: LoadArgs) => post({ type: "load", ...args }),
    [post],
  );
  const renderAt = useCallback(
    (t: number) => post({ type: "renderAt", t }),
    [post],
  );
  const bounds = useCallback(() => post({ type: "bounds" }), [post]);
  const backings = useCallback(
    (map: Record<string, string | null>) =>
      post({ type: "backings", backings: map }),
    [post],
  );
  const seek = useCallback(
    (t: number) => {
      const win = iframeRef.current?.contentWindow;
      if (!ready || !win) return;
      win.postMessage({ type: "seek", t }, targetOrigin);
    },
    [iframeRef, ready, targetOrigin],
  );
  const picture = useCallback(
    () =>
      ask({ type: "picture" }, PICTURE_TIMEOUT_MS).then((r) => {
        if (typeof r.svg !== "string") throw new Error("no picture");
        return r.svg;
      }),
    [ask],
  );
  const real = useMemo<OverlayFrame>(
    () => ({ ready, error, load, renderAt, bounds, backings, seek, picture }),
    [ready, error, load, renderAt, bounds, backings, seek, picture],
  );

  return fake ?? real;
}
